import {
	AccountLevelEnum,
	AccountStatusEnum,
	CarrierPaymentTypeEnum,
	MaerskColorPalette,
	PredefinedFiltersEnum,
	SnackBarVariants,
} from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import withSnackBar from '@app/components/core/message.component';
import ReportBalanceHistoryComponent from '@app/components/report/balance-history/balance-history.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import LinearProgressWithLabel from '@app/components/shared/linear-progress.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import { ChangePasswordNowLink } from '@app/constants/index';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { DashboardService } from '@app/services/dashboard.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Card, CardContent, CardHeader, Grid, Hidden, Link, Typography } from '@material-ui/core';
import Axios from 'axios';
import dayjs from 'dayjs';
import * as React from 'react';
import * as ReactChartJS from 'react-chartjs-2';
import { FormattedNumber, injectIntl } from 'react-intl';
import { withAxiosCancellation } from '../../core/axios-cancel.component';

type FilterType = 'startDate' | 'endDate';

const dashboardService: DashboardService = Injector.get(DashboardService);
const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);

const pageSizeDivisor = 60;
const defaultDateFilterGapInDays = 1;

class DashboardComponent extends React.Component<models.IDashboardComponentProps, models.IDashboardState> {
	private isPartnerAccount: boolean;

	constructor(props: models.IDashboardComponentProps) {
		super(props);
		this.isPartnerAccount = store.getState().authentication.user.account.level === AccountLevelEnum.Partner;
		this.getAccountDetail();
		this.state = models.initialDashboardState();
	}

	public componentDidMount() {
		this.bindFunction();
		if (!this.isPartnerAccount) {
			const page = this.getPageSize();
			this.getDashboardDetail(this.state.startDate, this.state.endDate, page);
		}
	}

	public componentDidUpdate(prevProps: models.IDashboardComponentProps, prevState: models.IDashboardState) {
		if (this.shouldFetchData(prevState)) {
			this.getDashboardDetail(this.state.startDate, this.state.endDate);
		}
	}

	public render() {
		let name = '';
		if (this.state.user) {
			const { firstName, lastName, account } = this.state.user;
			if (firstName && firstName === lastName) {
				name = firstName;
			} else if (firstName && firstName !== lastName) {
				name = firstName + ' ' + lastName;
			} else {
				name = account.name;
			}
		}

		return (
			<div>
				{this.changePasswordLink()}
				<Grid container={true} spacing={2}>
					<Grid item={true} xs={12} sm={12} md={12} lg={12}>
						<Card>
							<CardContent>
								<Grid container={true} alignItems='center' spacing={2}>
									<Grid item={true} lg={7} md={12} sm={12} xs={12}>
										<Grid container={true} justifyContent='center'>
											<Grid item={true} lg={11}>
												<span className='pr-1'>
													{this.props.intl.formatMessage({
														id: 'app.dashboard-welcome',
													})}
												</span>
												<span className='primary-color pr-1'>{name}</span>
											</Grid>
											{!this.isPartnerAccount && (
												<Grid item={true} lg={1}>
													<Grid container={true} justifyContent='flex-end' alignItems='center'>
														<Grid item={true} className={`${this.state.dataLoading ? 'spin-icon' : null} pointer`}>
															<Icon
																name='rotate'
																fontSize='large'
																onClick={() => {
																	const composedFunc = this.callAfterCancellingActiveRequests(() => {
																		this.getDashboardDetail(this.state.startDate, this.state.endDate);
																	});

																	composedFunc();
																}}
															/>
														</Grid>
													</Grid>
												</Grid>
											)}
										</Grid>
									</Grid>
									{!this.isPartnerAccount && (
										<>
											<Grid item={true} lg={2} md={12} sm={12} xs={12}>
												<PredefinedFilters
													startDate={this.state.startDate}
													endDate={this.state.endDate}
													onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
												/>
											</Grid>
											<Grid item={true} lg={3} md={12} sm={12} xs={12}>
												<Grid container={true} spacing={2}>
													<Grid item={true} lg={6} md={6} sm={6} xs={6}>
														<CustomDatePicker
															label={intlService.GetFormattedMessage('app.shipment.start-date-label')}
															maxDate={dayjs(this.state.endDate)
																.subtract(
																	this.state.predefinedFilterValue === PredefinedFiltersEnum.Custom
																		? defaultDateFilterGapInDays
																		: this.getMinDateDiff(this.state.startDate, this.state.endDate),
																	'day'
																)
																.endOf('day')
																.toDate()}
															disabled={this.state.predefinedFilterValue !== PredefinedFiltersEnum.Custom}
															value={dayjs(this.state.startDate).startOf('day')}
															strictCompareDates={true}
															onChange={async (date: dayjs.Dayjs) => {
																this.setStateAfterCancellingActiveRequests('startDate', date.toDate());
															}}
														/>
													</Grid>
													<Grid item={true} lg={6} md={6} sm={6} xs={6}>
														<CustomDatePicker
															label={intlService.GetFormattedMessage('app.shipment.end-date-label')}
															minDate={dayjs(this.state.startDate)
																.add(
																	this.state.predefinedFilterValue === PredefinedFiltersEnum.Custom
																		? defaultDateFilterGapInDays
																		: this.getMinDateDiff(this.state.startDate, this.state.endDate),
																	'day'
																)
																.startOf('day')
																.toDate()}
															maxDate={dayjs(Date.now()).startOf('day')}
															disabled={this.state.predefinedFilterValue !== PredefinedFiltersEnum.Custom}
															value={dayjs(this.state.endDate).startOf('day')}
															strictCompareDates={true}
															onChange={async (date: dayjs.Dayjs) => {
																this.setStateAfterCancellingActiveRequests('endDate', date.toDate());
															}}
														/>
													</Grid>
												</Grid>
											</Grid>
										</>
									)}
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
				{this.getDashboardView()}
			</div>
		);
	}

	private completeProfile = (): JSX.Element => {
		return (
			<Grid item={true} xs={12} sm={12} md={12} lg={12}>
				<Card square={true}>
					<CardContent>
						<div>
							{this.props.intl.formatMessage({
								id: 'app.dashboard-complete-profile',
							})}
						</div>
						<LinearProgressWithLabel value={this.state.profileCompleteness.percentage} />
						<br />
						<Grid container={true} spacing={3}>
							<Grid
								className='profile-completeness-grid'
								style={{ order: this.state.profileCompleteness.userInfoSetUpOrder }}
								item={true}
								xs={12}
								md={12}
							>
								<Grid container={true}>
									<Grid item={true} xs={6} md={8}>
										<Typography variant='subtitle2' color='inherit'>
											<strong>{`${this.props.intl.formatMessage({ id: 'app.dashboard-user-information' })}`}</strong>
										</Typography>
									</Grid>
									<Grid item={true} xs={6} md={4}>
										<Icon name='check-circle' color='primary' />
									</Grid>
								</Grid>
							</Grid>

							<Grid
								className='profile-completeness-grid'
								style={{ order: this.state.profileCompleteness.paymentSetUpOrder }}
								item={true}
								xs={12}
								md={12}
							>
								<Grid container={true}>
									<Grid item={true} xs={6} md={8}>
										<Typography variant='subtitle2' color='inherit'>
											<strong>{`${this.props.intl.formatMessage({ id: 'app.dashboard-payment-details' })}`}</strong>
										</Typography>
									</Grid>
									<Grid item={true} xs={6} md={4}>
										{this.state.profileCompleteness.isPaymentSetUp ? (
											<Icon name='check-circle' color='primary' />
										) : (
											<Link
												component='button'
												onClick={() => {
													history.push(models.Module.Billing.url, {
														extraParams: { isRoutedFrom: 'ProfileCompleteness' },
													});
												}}
											>
												{intlService.GetFormattedMessage('app.dashboard-click-to-complete')}
											</Link>
										)}
									</Grid>
								</Grid>
							</Grid>
							{this.state.profileCompleteness.isContainsOnlyePostageCarrier ? (
								<Grid
									className='profile-completeness-grid'
									style={{ order: this.state.profileCompleteness.shipFromAddressExistOrder }}
									item={true}
									xs={12}
									md={12}
								>
									<Grid container={true}>
										<Grid item={true} xs={6} md={8}>
											<Typography variant='subtitle2' color='inherit'>
												<strong>
													{`${this.props.intl.formatMessage({ id: 'app.dashboard-usps-epostage-shipper-details' })}`}
												</strong>
											</Typography>
										</Grid>
										<Grid item={true} xs={6} md={4}>
											{this.state.profileCompleteness.isShipFromAddressExist ? (
												<Icon name='check-circle' color='primary' />
											) : (
												<Link
													component='button'
													onClick={() => {
														history.push(`${models.Module.Apps.url}/app-detail`, {
															app: { appId: this.state.profileCompleteness.ePostageAppId },
															extraParams: { isRoutedFrom: 'ProfileCompleteness' },
														});
													}}
												>
													{intlService.GetFormattedMessage('app.dashboard-click-to-complete')}
												</Link>
											)}
										</Grid>
									</Grid>
								</Grid>
							) : null}
							<Grid
								className='profile-completeness-grid'
								style={{ order: this.state.profileCompleteness.carrierAccountSetUpOrder }}
								item={true}
								xs={12}
								md={12}
							>
								<Grid container={true}>
									<Grid item={true} xs={8} md={8}>
										<Typography variant='subtitle2' color='inherit'>
											<strong>
												{this.state.profileCompleteness.isCarrierAccountSetUp}
												{`${this.props.intl.formatMessage({ id: 'app.dashboard-carrier-account' })}`}
											</strong>
										</Typography>
									</Grid>
									<Grid item={true} xs={8} md={4}>
										{this.state.profileCompleteness.isCarrierAccountSetUp ? (
											<Icon name='check-circle' color='primary' />
										) : (
											<Link
												component='button'
												onClick={() => {
													history.push(models.Module.Carriers.url);
												}}
											>
												{intlService.GetFormattedMessage('app.dashboard-click-to-complete')}
											</Link>
										)}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Grid>
		);
	};

	private changePasswordLink = (): JSX.Element => {
		const doesNotHavePasswordChangeDueDate = !this.state.user?.passwordChangeDueDate;
		const passwordChangeDueDateHasNotPassed = dayjs().isBefore(this.state.user?.passwordChangeDueDate, 'day');

		if (this.isPartnerAccount || doesNotHavePasswordChangeDueDate || passwordChangeDueDateHasNotPassed) {
			return null;
		}

		return (
			<Grid container={true} spacing={2}>
				<Grid item={true} xs={12} sm={12} md={12} lg={12}>
					<Card>
						<CardContent>
							<Grid container={true} alignItems='center' spacing={2}>
								<Grid item={true}>
									<span className='pr-1'>
										{`${this.props.intl.formatMessage({
											id: 'app.snackbar.password-change-due-date-expired',
										})}`}
									</span>
									<Link
										href='#'
										className='red-color'
										underline='always'
										onClick={() => {
											history.push('/account', {
												extraParams: { isRoutedFrom: ChangePasswordNowLink },
											});
										}}
									>
										{this.props.intl.formatMessage({
											id: 'app.dashboard.change-password-link',
										})}
									</Link>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		);
	};

	// Return dashboard view based on accountLevel
	private getDashboardView = (): JSX.Element => {
		if (this.isPartnerAccount) {
			return null;
		}

		return this.getPHDashboard();
	};

	private getMinDateDiff = (startDate: Date, endDate: Date) => {
		return dayjs(endDate).diff(startDate, 'day');
	};

	// Get dashboard details
	private async getDashboardDetail(startDate: Date, endDate: Date, pageSize?: number) {
		try {
			let pageToken = sharedService.generatePaginationToken(this.state.currentPage - 2);

			if (this.state.currentPage === 0) {
				pageToken = undefined;
			}
			let balanceHistory: models.IPagedResult<models.IAccountTransaction> = {
				results: [],
				nextPage: '',
				previousPage: '',
				pageSize: 0,
				totalResults: 0,
				totalPages: 0,
			};

			this.setState({
				dataLoading: true,
				...balanceHistory,
			});
			const day: number = this.getMinDateDiff(startDate, endDate);
			const result = await dashboardService.getDashboard(
				pageToken,
				pageSize ?? this.state.accountStatementPageSize,
				day,
				endDate,
				this.props.axiosCancellationToken
			);
			balanceHistory = result.billingHistory;

			this.setState({
				...balanceHistory,
				fees: {
					labels: result.shipmentData.map((x) => x.date.toString()),
					datasets: [
						{
							...this.state.fees.datasets[0],
							data: result.shipmentData.map((x) => x.prePayShipmentFee),
							borderColor: MaerskColorPalette.MaerskBlue,
							pointBackgroundColor: MaerskColorPalette.MaerskBlue,
						},
					],
				},
				user: {
					...this.state.user,
					firstName: result.firstName,
					lastName: result.lastName,
					account: {
						...this.state.user.account,
						name: result.companyName,
					},
				},
				todayTransactionPrePay: result.shipmentData[result.shipmentData.length - 1].prePayShipmentCount,
				todayTransactionFeesPrePay: result.shipmentData[result.shipmentData.length - 1].prePayShipmentFee,
				todayTransactionPostPay: result.shipmentData[result.shipmentData.length - 1].postPayShipmentCount,
				todayTransactionFeesPostPay: result.shipmentData[result.shipmentData.length - 1].postPayShipmentFee,
				startDate,
				endDate,
				day,
			});
			this.setState({
				dataLoading: false,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// Get account details
	private async getAccountDetail() {
		try {
			const result: models.IUser = await accountService.getOwnUserRecord(this.props.axiosCancellationToken);
			const data = await accountService.getProfileCompletenessInfo(this.props.axiosCancellationToken);

			this.assignOrderInProfileCompleteness(data);

			this.setState({
				user: result,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	}

	private bindFunction() {
		this.getDashboardDetail = this.getDashboardDetail.bind(this);
		this.getAccountDetail = this.getAccountDetail.bind(this);
	}

	private assignOrderInProfileCompleteness(data: models.IProfileCompleteness) {
		data.userInfoSetUpOrder = 4;
		let increasingOrder = 1;
		let decreasingOrder = 3;
		if (data.isPaymentSetUp) {
			data.paymentSetUpOrder = decreasingOrder;
			decreasingOrder--;
		} else {
			data.paymentSetUpOrder = increasingOrder;
			increasingOrder++;
		}

		if (data.isShipFromAddressExist) {
			data.shipFromAddressExistOrder = decreasingOrder;
			decreasingOrder--;
		} else {
			data.shipFromAddressExistOrder = increasingOrder;
			increasingOrder++;
		}

		if (data.isCarrierAccountSetUp) {
			data.carrierAccountSetUpOrder = decreasingOrder;
			decreasingOrder--;
		} else {
			data.carrierAccountSetUpOrder = increasingOrder;
			increasingOrder++;
		}

		this.setState({
			profileCompleteness: data,
		});
	}

	/**
	 * Gives the page size
	 * @returns Give page size from window height
	 */
	private getPageSize = (): number => {
		const height = window.innerHeight;
		const page = this.getRowsByContainerHeight(height);
		this.setState({
			accountStatementPageSize: page,
		});
		return page;
	};

	/**
	 * Calculate no of rows according to page height
	 * @returns Calculate page size
	 */
	private getRowsByContainerHeight = (height: number): number => {
		// Calculate no of rows according to page height
		return Math.floor(height / pageSizeDivisor);
	};

	private getPHDashboard = () => {
		const { status: accountStatus } = store.getState().authentication;
		return (
			<Grid container={true} direction='row' spacing={2}>
				<Grid item={true} xs={12} sm={12} md={6} lg={6}>
					<Grid container={true} direction='column' spacing={2}>
						{accountStatus !== AccountStatusEnum.Closed && this.state.profileCompleteness.percentage < 100
							? this.completeProfile()
							: null}
						<Grid item={true} xs={12} sm={12} md={12} lg={12}>
							<Card square={true}>
								<CardContent>
									<div>
										{this.props.intl.formatMessage({
											id: 'app.dashboard-fees',
										})}
									</div>
									<ReactChartJS.Line data={this.state.fees} width={500} height={250} />
								</CardContent>
							</Card>
						</Grid>
						<Grid item={true} xs={12} sm={12} md={12} lg={12}>
							{this.paymentWidgetCard()}
						</Grid>
						<Grid item={true} xs={12} sm={12} md={12} lg={12}>
							{this.paymentWidgetCard(CarrierPaymentTypeEnum.Postpaid)}
						</Grid>
					</Grid>
				</Grid>
				<Hidden only={['sm', 'xs']}>
					<Grid item={true} xs={12} sm={12} md={6} lg={6}>
						<ReportBalanceHistoryComponent
							contained={true}
							pageSize={this.state.accountStatementPageSize}
							heading={models.Module.Reports.children.ReportAccountStatement.toString()}
							rows={this.state.results}
							startDate={this.state.startDate}
							endDate={this.state.endDate}
							loading={this.state.dataLoading}
							totalPages={this.state.totalPages}
							siblingCount={1}
							totalResults={this.state.totalResults}
							updateCurrentPage={(currentPage: number) => {
								this.setState({
									currentPage,
								});
							}}
						/>
					</Grid>
				</Hidden>
			</Grid>
		);
	};

	private paymentWidgetCard = (paymentType?: CarrierPaymentTypeEnum) => {
		return (
			<Card square={true}>
				<CardHeader
					className='pb-0'
					title={
						paymentType === CarrierPaymentTypeEnum.Postpaid
							? this.props.intl.formatMessage({ id: 'app.dashboard-postpay-widget-title' })
							: this.props.intl.formatMessage({ id: 'app.dashboard-prepay-widget-title' })
					}
					titleTypographyProps={{ variant: 'body2' }}
				/>
				<CardContent>
					<Grid container={true} direction='row' spacing={2}>
						<Grid className='text-end' item={true} xl={6} lg={6} md={6} sm={6}>
							<Icon name='digital-service' fontSize='extra-large' />
						</Grid>
						<Grid item={true} xl={6} lg={6} md={6} sm={6}>
							<Grid container={true} direction='column'>
								<Grid item={true} className='bold'>
									<Typography variant='h4'>
										<FormattedNumber
											value={
												(paymentType === CarrierPaymentTypeEnum.Postpaid
													? this.state.todayTransactionPostPay
													: this.state.todayTransactionPrePay) ?? 0
											}
										/>
									</Typography>
								</Grid>
								<Grid item={true}>
									{this.props.intl.formatMessage({
										id: 'app.dashboard-transactions',
									})}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid container={true} direction='row' spacing={2}>
						<Grid className='text-end' item={true} xl={6} lg={6} md={6} sm={6}>
							<Icon name='quote' fontSize='extra-large' />
						</Grid>
						<Grid item={true} xl={6} lg={6} md={6} sm={6}>
							<Grid container={true}>
								<Grid item={true} xs={12} className='bold'>
									<Typography variant='h4'>
										$
										<FormattedNumber
											value={
												(paymentType === CarrierPaymentTypeEnum.Postpaid
													? this.state.todayTransactionFeesPostPay
													: this.state.todayTransactionFeesPrePay) ?? 0
											}
											maximumFractionDigits={2}
											minimumFractionDigits={2}
										/>
									</Typography>
								</Grid>
								<Grid item={true} xs={12}>
									{this.props.intl.formatMessage({
										id: 'app.dashboard-transaction-fees',
									})}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		);
	};

	private loadDataForPredefinedFilters = async (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			startDate,
			endDate,
			currentPage: 1,
			predefinedFilterValue: filterValue,
		});
	};

	private shouldFetchData = (prevState: models.IDashboardState): boolean => {
		return (
			(prevState.startDate !== this.state.startDate ||
				prevState.endDate !== this.state.endDate ||
				prevState.currentPage !== this.state.currentPage) &&
			!this.props.isRequestCancelled
		);
	};

	private setStateAfterCancellingActiveRequests = (key: FilterType, value: any) => {
		const composedFunc = this.callAfterCancellingActiveRequests(() => {
			this.setState((prevState: models.IDashboardState) => ({
				...prevState,
				[key]: value,
				currentPage: 1,
			}));
		});

		composedFunc();
	};

	private callAfterCancellingActiveRequests = (callback: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			callback(...args);
		};
	};
}

export default withAxiosCancellation(withSnackBar(injectIntl(DashboardComponent)));



// WEBPACK FOOTER //
// ./src/components/dashboard/dashboard/dashboard.component.tsx